import { observer } from 'mobx-react';
import { Drawer, Button, Typography, Radio } from 'antd';
import { FilterOutlined, ClearOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';

const { Text } = Typography;

// ── Filter state shape ────────────────────────────────────────────────────

export type AudienceFilter = 'any' | 'patient' | 'careTeam' | 'both' | 'none';
export type TriStateFilter = 'any' | 'on' | 'off';

// The status is not here: it is the editor's own visible toggle (All · Active · Draft · Inactive · Archived), 2 Nov 2026.
// It was an "Active agents only" switch that read a field the server never sends, so it filtered nothing.
export interface PeerCandidateFilters {
  audience: AudienceFilter;
  router: TriStateFilter;
  consults: TriStateFilter;
}

export const DEFAULT_FILTERS: PeerCandidateFilters = {
  audience: 'any',
  router: 'any',
  consults: 'any',
};

export function activeFilterCount(f: PeerCandidateFilters): number {
  let n = 0;
  if (f.audience !== 'any') n++;
  if (f.router !== 'any') n++;
  if (f.consults !== 'any') n++;
  return n;
}

// ── Sub-drawer ────────────────────────────────────────────────────────────
// Stacks on top of the main PeerEditorDrawer. Lets the user narrow the
// candidate roster by audience, router state, consultation enablement, or
// inactive agents. Apply-on-change so the underlying list filters live.

interface PeerCandidateFilterDrawerProps {
  open: boolean;
  filters: PeerCandidateFilters;
  onChange: (next: PeerCandidateFilters) => void;
  onClose: () => void;
}

export const PeerCandidateFilterDrawer = observer(function PeerCandidateFilterDrawer({
  open,
  filters,
  onChange,
  onClose,
}: PeerCandidateFilterDrawerProps) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const count = activeFilterCount(filters);

  function patch(next: Partial<PeerCandidateFilters>): void {
    onChange({ ...filters, ...next });
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <FilterOutlined /> Filter candidates
          {count > 0 && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                background: colors.ink[600],
                color: '#FFFFFF',
                padding: '2px 8px',
                borderRadius: 999,
              }}
            >
              {count}
            </span>
          )}
        </span>
      }
      width={380}
      destroyOnClose={false}
      mask={false}
      styles={{ body: { padding: '20px 22px' } }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            type="text"
            size="small"
            icon={<ClearOutlined />}
            disabled={count === 0}
            onClick={() => onChange(DEFAULT_FILTERS)}
          >
            Reset
          </Button>
          <Button type="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      }
    >
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 18 }}>
        Narrow the candidate list. Filters apply live.
      </Text>

      <FilterSection isDark={isDark} label="Audience">
        <Radio.Group
          value={filters.audience}
          onChange={(e) => patch({ audience: e.target.value as AudienceFilter })}
          optionType="button"
          buttonStyle="solid"
          size="small"
        >
          <Radio.Button value="any">Any</Radio.Button>
          <Radio.Button value="patient">Patient</Radio.Button>
          <Radio.Button value="careTeam">Care team</Radio.Button>
          <Radio.Button value="both">Both</Radio.Button>
          <Radio.Button value="none">Hidden</Radio.Button>
        </Radio.Group>
      </FilterSection>

      <FilterSection isDark={isDark} label="Skill router">
        <Radio.Group
          value={filters.router}
          onChange={(e) => patch({ router: e.target.value as TriStateFilter })}
          optionType="button"
          buttonStyle="solid"
          size="small"
        >
          <Radio.Button value="any">Any</Radio.Button>
          <Radio.Button value="on">On</Radio.Button>
          <Radio.Button value="off">Off</Radio.Button>
        </Radio.Group>
      </FilterSection>

      <FilterSection isDark={isDark} label="Peer consultation">
        <Radio.Group
          value={filters.consults}
          onChange={(e) => patch({ consults: e.target.value as TriStateFilter })}
          optionType="button"
          buttonStyle="solid"
          size="small"
        >
          <Radio.Button value="any">Any</Radio.Button>
          <Radio.Button value="on">Enabled</Radio.Button>
          <Radio.Button value="off">Disabled</Radio.Button>
        </Radio.Group>
      </FilterSection>

    </Drawer>
  );
});

// ── Section helper ────────────────────────────────────────────────────────

function FilterSection({
  label,
  isDark,
  children,
}: {
  label: string;
  isDark: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: isDark ? colors.slate[400] : colors.slate[500],
          marginBottom: 10,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}
